import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { functionError } from '@/lib/functionError';
import { GroupReports } from './GroupReports';
import { db } from '@/lib/db';
import { toast } from '@/stores/toastStore';
import { useRows } from '@/hooks/useLive';
import { useAuthStore } from '@/stores/authStore';
import { date as fmtDate, dateTime, humanise } from '@/lib/format';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { StatusChip } from '@/components/ui/StatusChip';
import {
  CheckboxInput, NumberInput, SelectInput, TextInput,
} from '@/components/ui/Field';
import { QUARRY_SITE_ID } from '@/lib/fleet';
import type { Setting, Site } from '@/types/models';

interface Recipient {
  id: string;
  email: string;
  full_name: string | null;
  role_note: string | null;
  daily: boolean;
  monthly: boolean;
  is_active: boolean;
}

interface Delivery {
  id: string;
  site_id: string;
  report_type: string;
  report_date: string;
  status: 'sent' | 'failed' | 'skipped';
  recipients: string[];
  subject: string | null;
  detail: string | null;
  triggered_by: string;
  created_at: string;
}

/** Known providers, so nobody has to remember that Office 365 is 587 and
 *  STARTTLS while an implicit-TLS relay is 465. */
const PRESETS: Record<string, { host: string; port: number; security: string; note: string }> = {
  gmail: {
    host: 'smtp.gmail.com', port: 587, security: 'starttls',
    note: 'Google requires an App Password — your normal account password will be refused. Create one under Google Account → Security → 2-Step Verification → App passwords.',
  },
  office365: {
    host: 'smtp.office365.com', port: 587, security: 'starttls',
    note: 'The From address must be the licensed mailbox you authenticate as, and SMTP AUTH has to be enabled for it in the Microsoft 365 admin centre.',
  },
  resend: {
    host: 'smtp.resend.com', port: 465, security: 'tls',
    note: 'Username is literally "resend"; the password is your Resend API key. The sending domain must be verified first.',
  },
  sendgrid: {
    host: 'smtp.sendgrid.net', port: 587, security: 'starttls',
    note: 'Username is literally "apikey"; the password is the API key itself.',
  },
};

const SECURITY_OPTIONS = [
  { value: 'starttls', label: 'STARTTLS — usually port 587' },
  { value: 'tls', label: 'TLS / SSL — usually port 465' },
  { value: 'none', label: 'None — unencrypted, port 25' },
];

const FIELDS: Array<{ key: string; label: string; kind: 'text' | 'number' | 'select' | 'bool'; help?: string }> = [
  {
    key: 'app_url', label: 'Site address', kind: 'text',
    help: 'Where this app is served from, e.g. https://suremix.iotechnam.com '
      + '(no trailing slash). Invitation and password-reset links point here, so '
      + 'nobody can be invited until it is set.',
  },
  { key: 'smtp_host', label: 'SMTP host', kind: 'text' },
  { key: 'smtp_port', label: 'Port', kind: 'number' },
  { key: 'smtp_security', label: 'Connection security', kind: 'select' },
  {
    key: 'smtp_username', label: 'Username', kind: 'text',
    help: 'Usually the full sending address.',
  },
  { key: 'smtp_from_email', label: 'From address', kind: 'text', help: 'Most providers require this to match the username.' },
  { key: 'smtp_from_name', label: 'From name', kind: 'text' },
  {
    key: 'report_send_hour', label: 'Send at (local hour)', kind: 'number',
    help: 'Applies to both reports. Changing it needs the schedule re-applied — '
      + 'see docs/09-notifications.md.',
  },
  {
    key: 'monthly_send_day', label: 'Monthly send day', kind: 'number',
    help: 'Day of the month last month\'s report goes out. 1–28, because not every '
      + 'month has a 29th. Changing it needs the schedule re-applied.',
  },
  { key: 'report_timezone', label: 'Site timezone', kind: 'text' },
];

const SECRET_COMMAND = "npx supabase secrets set SMTP_PASSWORD='your-password-here'";

export function NotificationsPage() {
  const settings = useRows<Setting>('settings');
  const sites = useRows<Site>('sites');
  const isAdmin = useAuthStore((s) => s.hasRole('manager', 'admin'));

  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [monthly, setMonthly] = useState(false);

  const [recipients, setRecipients] = useState<Recipient[] | null>(null);
  const [deliveries, setDeliveries] = useState<Delivery[] | null>(null);
  const [busy, setBusy] = useState(false);

  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('');

  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);

  const smtp = useMemo(() => {
    const m = new Map<string, Setting>();
    for (const s of settings ?? []) if (s.category === 'notification') m.set(s.key, s);
    return m;
  }, [settings]);

  useEffect(() => {
    if (smtp.size === 0) return;
    setDraft((d) => {
      const next = { ...d };
      for (const [k, s] of smtp) if (!(k in next)) next[k] = s.value;
      return next;
    });
    const on = smtp.get('notifications_enabled');
    if (on) setEnabled(Boolean(on.value));
    const monthlyOn = smtp.get('monthly_enabled');
    if (monthlyOn) setMonthly(Boolean(monthlyOn.value));
  }, [smtp]);

  const load = useCallback(async () => {
    const [{ data: r }, { data: d }] = await Promise.all([
      // The quarry's list. Each branch keeps its own (Concrete → Branch
      // settings, 0039); the log below shows every site's sends.
      supabase.from('report_recipients').select('*').eq('site_id', QUARRY_SITE_ID)
        .is('deleted_at', null).order('email'),
      supabase.from('report_deliveries').select('*').order('created_at', { ascending: false }).limit(20),
    ]);
    setRecipients((r ?? []) as Recipient[]);
    setDeliveries((d ?? []) as Delivery[]);
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function saveSetting(key: string, value: unknown) {
    const { data, error } = await supabase
      .from('settings').update({ value }).eq('key', key).select().single();
    if (error) throw new Error(error.message);
    await db.settings.put(data as Setting);
  }

  async function saveSmtp() {
    setSavingSmtp(true);
    try {
      for (const f of FIELDS) {
        const current = smtp.get(f.key);
        if (!current) continue;
        if (JSON.stringify(draft[f.key]) === JSON.stringify(current.value)) continue;
        await saveSetting(f.key, draft[f.key]);
      }
      toast.success('Mail settings saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingSmtp(false);
    }
  }

  async function toggleEnabled(on: boolean) {
    setEnabled(on);
    try {
      await saveSetting('notifications_enabled', on);
      toast.success(on ? 'Daily report will be sent' : 'Daily report paused');
    } catch (err) {
      setEnabled(!on);
      toast.error(err instanceof Error ? err.message : String(err));
    }
  }

  async function toggleMonthly(on: boolean) {
    setMonthly(on);
    try {
      await saveSetting('monthly_enabled', on);
      toast.success(on ? 'Monthly report will be sent' : 'Monthly report paused');
    } catch (err) {
      setMonthly(!on);
      toast.error(err instanceof Error ? err.message : String(err));
    }
  }

  function applyPreset(name: string | null) {
    const p = name ? PRESETS[name] : null;
    if (!p) return;
    setDraft((d) => ({ ...d, smtp_host: p.host, smtp_port: p.port, smtp_security: p.security }));
    toast.success(`${humanise(name!)} settings filled in — check the username and From address.`);
  }

  const activePreset = useMemo(() => {
    const host = String(draft.smtp_host ?? '');
    return Object.entries(PRESETS).find(([, p]) => p.host === host)?.[0] ?? null;
  }, [draft.smtp_host]);

  async function addRecipient() {
    if (!newEmail.trim()) return;
    setBusy(true);
    const { error } = await supabase.from('report_recipients').insert({
      site_id: QUARRY_SITE_ID,
      email: newEmail.trim().toLowerCase(),
      full_name: newName.trim() || null,
      role_note: newRole.trim() || null,
      daily: true,
      monthly: false,
      is_active: true,
    });
    setBusy(false);
    if (error) {
      toast.error(error.code === '23505'
        ? 'That address is already on the list.'
        : error.code === '23514'
          ? 'That does not look like an email address.'
          : error.message);
      return;
    }
    setNewEmail(''); setNewName(''); setNewRole('');
    toast.success('Recipient added');
    await load();
  }

  async function updateRecipient(id: string, patch: Partial<Recipient>) {
    const { error } = await supabase.from('report_recipients').update(patch).eq('id', id);
    if (error) { toast.error(error.message); return; }
    await load();
  }

  async function removeRecipient(id: string) {
    const { error } = await supabase
      .from('report_recipients').update({ deleted_at: new Date().toISOString() }).eq('id', id);
    if (error) { toast.error(error.message); return; }
    toast.success('Recipient removed');
    await load();
  }

  async function sendTest(which: 'daily' | 'monthly' = 'daily') {
    if (!testTo.trim()) return;
    setTesting(true);
    try {
      // One function sends both; the body says which. Its name is still
      // daily-report-email because the schedule and app.edge_config point
      // at that URL.
      const { data, error } = await supabase.functions.invoke('daily-report-email', {
        body: {
          test_to: testTo.trim(),
          triggered_by: 'manual',
          ...(which === 'monthly' ? { report: 'monthly' } : {}),
        },
      });
      if (error) {
        // The function answered, and refused: say what it said.
        const said = await functionError(error);
        if (said) { toast.error(said); return; }
        throw error;
      }
      const res = data as { status?: string; error?: string };
      if (res?.status === 'sent') toast.success(`${which === 'monthly' ? 'Monthly' : 'Daily'} test sent to ${testTo.trim()}`);
      else toast.error(res?.error ?? `Not sent: ${res?.status ?? 'unknown result'}`);
    } catch (err) {
      toast.error(
        `Could not reach the mail function. Deploy it with "npx supabase functions deploy daily-report-email". (${
          err instanceof Error ? err.message : String(err)})`,
      );
    } finally {
      setTesting(false);
      await load();
    }
  }

  if (!settings) return <div className="centered"><span className="spinner" /></div>;

  if (smtp.size === 0) {
    return (
      <Panel title="Report delivery">
        <p className="small muted" style={{ marginBottom: 0 }}>
          The notification settings are not in this database yet. Apply migration
          <code> 0020_notifications.sql</code> with <code>npx supabase db push</code>.
        </p>
      </Panel>
    );
  }

  const dirty = FIELDS.some((f) => {
    const s = smtp.get(f.key);
    return s && JSON.stringify(draft[f.key]) !== JSON.stringify(s.value);
  });

  return (
    <div className="col" style={{ gap: 'var(--sp-3)' }}>
      <Panel
        title="Daily report delivery"
        actions={
          <StatusChip tone={enabled ? 'green' : 'grey'}>
            {enabled ? 'On' : 'Paused'}
          </StatusChip>
        }
      >
        <p className="small muted" style={{ marginTop: 0 }}>
          At {String(draft.report_send_hour ?? 5)}:00 each morning the previous day's
          production report is emailed to the people below. It is built by the same
          database function the Reports screen uses, so the inbox and the dashboard
          cannot disagree.
        </p>
        <CheckboxInput
          id="n-enabled"
          label="Send the daily report automatically"
          value={enabled}
          disabled={!isAdmin}
          onChange={(v) => void toggleEnabled(v)}
        />
      </Panel>

      <Panel
        title="Monthly report delivery"
        actions={
          <StatusChip tone={monthly ? 'green' : 'grey'}>
            {monthly ? 'On' : 'Paused'}
          </StatusChip>
        }
      >
        <p className="small muted" style={{ marginTop: 0 }}>
          On day {String(draft.monthly_send_day ?? 1)} of each month, at{' '}
          {String(draft.report_send_hour ?? 5)}:00, the month that has just ended is
          summarised and emailed to everyone ticked <strong>Monthly</strong> below —
          production against target, downtime by machine and by cause, maintenance,
          blasting and parts. Separate from the daily report: either can run without
          the other.
        </p>
        <CheckboxInput
          id="n-monthly"
          label="Send the monthly report automatically"
          value={monthly}
          disabled={!isAdmin}
          onChange={(v) => void toggleMonthly(v)}
        />
      </Panel>

      <Panel title="Mail server">
        <div className="issue issue--warn" style={{ marginBottom: 'var(--sp-3)', display: 'block' }}>
          <strong>The password is not stored here.</strong>
          <p className="small" style={{ margin: '4px 0 8px' }}>
            Every row of the settings table is readable by any signed-in user, so a
            password kept here would be a password handed to every operator with a
            tablet. Set it once as an Edge Function secret instead — it never leaves
            Supabase:
          </p>
          <code className="code-block">{SECRET_COMMAND}</code>
          <Button
            variant="ghost"
            style={{ minHeight: 30, marginTop: 6 }}
            onClick={() => {
              void navigator.clipboard?.writeText(SECRET_COMMAND)
                .then(() => toast.success('Command copied'))
                .catch(() => toast.error('Could not copy — select the text instead'));
            }}
          >
            Copy command
          </Button>
        </div>

        <div className="form-grid">
          <SelectInput
            id="n-preset"
            label="Provider"
            value={activePreset}
            options={[
              { value: 'gmail', label: 'Gmail / Google Workspace' },
              { value: 'office365', label: 'Microsoft 365' },
              { value: 'resend', label: 'Resend' },
              { value: 'sendgrid', label: 'SendGrid' },
            ]}
            placeholder="— custom —"
            disabled={!isAdmin}
            onChange={applyPreset}
            help="Fills in the host, port and security. Everything stays editable."
          />
          <div />

          {FIELDS.map((f) => {
            const s = smtp.get(f.key);
            if (!s) return null;
            const v = draft[f.key];
            if (f.kind === 'number') {
              return (
                <NumberInput
                  key={f.key} id={`n-${f.key}`} label={f.label} help={f.help}
                  value={v == null ? null : Number(v)} disabled={!isAdmin}
                  onChange={(x) => setDraft((d) => ({ ...d, [f.key]: x }))}
                />
              );
            }
            if (f.kind === 'select') {
              return (
                <SelectInput
                  key={f.key} id={`n-${f.key}`} label={f.label} help={f.help}
                  value={v == null ? null : String(v)} options={SECURITY_OPTIONS}
                  placeholder="" disabled={!isAdmin}
                  onChange={(x) => setDraft((d) => ({ ...d, [f.key]: x }))}
                />
              );
            }
            return (
              <TextInput
                key={f.key} id={`n-${f.key}`} label={f.label} help={f.help}
                value={v == null ? '' : String(v)} disabled={!isAdmin}
                onChange={(x) => setDraft((d) => ({ ...d, [f.key]: x }))}
              />
            );
          })}
        </div>

        {activePreset && (
          <p className="small muted">{PRESETS[activePreset].note}</p>
        )}

        {isAdmin && (
          <div className="row" style={{ gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
            <Button loading={savingSmtp} disabled={!dirty} onClick={() => void saveSmtp()}>
              Save mail settings
            </Button>
            {dirty && (
              <Button variant="ghost" onClick={() => {
                const next: Record<string, unknown> = {};
                for (const [k, s] of smtp) next[k] = s.value;
                setDraft(next);
              }}>Reset</Button>
            )}
          </div>
        )}
      </Panel>

      <Panel title={`Quarry recipients · ${recipients?.length ?? 0}`}>
        <p className="small muted" style={{ marginTop: 0 }}>
          The quarry's reports go to this list. Each concrete branch keeps its own list for its
          daily email, under Concrete → Branch settings.
        </p>
        {recipients === null ? (
          <div className="centered"><span className="spinner" /></div>
        ) : recipients.length === 0 ? (
          <p className="small muted" style={{ marginTop: 0 }}>
            Nobody is on the list yet, so nothing will be sent.
          </p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Email</th><th>Name</th><th>Role</th>
                <th style={{ textAlign: 'center' }}>Daily</th>
                <th style={{ textAlign: 'center' }}>Monthly</th>
                <th style={{ textAlign: 'center' }}>Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {recipients.map((r) => (
                <tr key={r.id}>
                  <td><strong>{r.email}</strong></td>
                  <td>{r.full_name ?? '—'}</td>
                  <td>{r.role_note ?? '—'}</td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={r.daily} disabled={!isAdmin}
                      aria-label={`Daily report to ${r.email}`}
                      onChange={(e) => void updateRecipient(r.id, { daily: e.target.checked })} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={r.monthly} disabled={!isAdmin}
                      aria-label={`Monthly report to ${r.email}`}
                      onChange={(e) => void updateRecipient(r.id, { monthly: e.target.checked })} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="checkbox" checked={r.is_active} disabled={!isAdmin}
                      aria-label={`${r.email} active`}
                      onChange={(e) => void updateRecipient(r.id, { is_active: e.target.checked })} />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {isAdmin && (
                      <button className="btn btn--ghost" style={{ minHeight: 28 }}
                        onClick={() => void removeRecipient(r.id)}>Remove</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {isAdmin && (
          <div className="form-grid" style={{ marginTop: 'var(--sp-3)' }}>
            <TextInput id="n-new-email" label="Email" type="email"
              value={newEmail} onChange={setNewEmail} />
            <TextInput id="n-new-name" label="Name" value={newName} onChange={setNewName} />
            <TextInput id="n-new-role" label="Role" value={newRole} onChange={setNewRole}
              help="Quarry manager, owner, maintenance lead…" />
            <div style={{ display: 'flex', alignItems: 'flex-start', paddingTop: 22 }}>
              <Button variant="secondary" loading={busy} disabled={!newEmail.trim()}
                onClick={() => void addRecipient()}>Add recipient</Button>
            </div>
          </div>
        )}
      </Panel>

      {isAdmin && (
        <Panel title="Send a test">
          <p className="small muted" style={{ marginTop: 0 }}>
            Sends the real report to one address, marked [TEST] — yesterday's for the
            daily, last month's for the monthly. This is the only way to know the
            password and the host are right: a schedule that fails at 05:00 tells
            nobody. A test ignores the recipient list and the on/off switch, so it
            works before either report is turned on.
          </p>
          <div className="form-grid">
            <TextInput id="n-test" label="Send to" type="email" value={testTo} onChange={setTestTo} />
            <div style={{ display: 'flex', alignItems: 'flex-start', paddingTop: 22, gap: 'var(--sp-2)' }}>
              <Button loading={testing} disabled={!testTo.trim()}
                onClick={() => void sendTest('daily')}>
                Send daily test
              </Button>
              <Button variant="secondary" loading={testing} disabled={!testTo.trim()}
                onClick={() => void sendTest('monthly')}>
                Send monthly test
              </Button>
            </div>
          </div>
        </Panel>
      )}

      <GroupReports canEdit={isAdmin} onSent={() => void load()} />

      <Panel title="Delivery log" flush>
        {deliveries === null ? (
          <div className="centered" style={{ padding: 'var(--sp-4)' }}><span className="spinner" /></div>
        ) : deliveries.length === 0 ? (
          <p className="small muted" style={{ padding: 'var(--sp-3)', marginBottom: 0 }}>
            Nothing has been sent yet.
          </p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>When</th><th>For</th><th>Type</th><th>Status</th>
                  <th>Recipients</th><th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map((d) => (
                  <tr key={d.id}>
                    <td>{dateTime(d.created_at)}</td>
                    <td>{fmtDate(d.report_date)}</td>
                    <td>
                      {d.report_type === 'branch'
                        ? `Daily · ${sites?.find((s) => s.id === d.site_id)?.name ?? 'branch'}`
                        : d.report_type === 'test' && d.site_id !== QUARRY_SITE_ID
                          ? `Test · ${sites?.find((s) => s.id === d.site_id)?.name ?? 'branch'}`
                          : d.report_type === 'group' ? 'Group daily'
                            : d.report_type === 'group_monthly' ? 'Group monthly'
                              : humanise(d.report_type)}
                      {d.triggered_by === 'manual' ? ' · manual' : ''}
                    </td>
                    <td>
                      <StatusChip tone={d.status === 'sent' ? 'green' : d.status === 'failed' ? 'red' : 'grey'}>
                        {humanise(d.status)}
                      </StatusChip>
                    </td>
                    <td className="small">{d.recipients.join(', ') || '—'}</td>
                    <td className="small">{d.detail ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
